import React, { useEffect, useState, useCallback } from "react";
import { FileBarChart, Download, ShoppingBag, Users, Boxes } from "lucide-react";
import api from "../lib/api";
import { Card, PageHeader, PageLoader, Badge, Button } from "../lib/ui";
import { fmtRp, fmtNum, STOCK_STATUS } from "../lib/constants";
import { useT } from "../lib/i18n";

const todayStr = () => new Date().toISOString().slice(0, 10);
const monthStart = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };

function exportCsv(filename, headers, rows) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = [headers.map(esc).join(","), ...rows.map((r) => r.map(esc).join(","))].join("\n");
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

const TABS = [
  { key: "expense", label: "Purchase Expense", icon: ShoppingBag },
  { key: "supplier", label: "Supplier Spending", icon: Users },
  { key: "valuation", label: "Inventory Valuation", icon: Boxes },
];

export default function Reports() {
  const { t } = useT();
  const [tab, setTab] = useState("expense");
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(todayStr());
  const [expense, setExpense] = useState(null);
  const [supplier, setSupplier] = useState(null);
  const [valuation, setValuation] = useState(null);

  const loadRange = useCallback(() => {
    const p = new URLSearchParams();
    if (from) p.set("date_from", from);
    if (to) p.set("date_to", to);
    setExpense(null); setSupplier(null);
    api.get(`/reports/purchase-expense?${p}`).then(({ data }) => setExpense(data));
    api.get(`/reports/supplier-spending?${p}`).then(({ data }) => setSupplier(data));
  }, [from, to]);

  useEffect(() => { loadRange(); }, [loadRange]);
  useEffect(() => { api.get("/reports/inventory-valuation").then(({ data }) => setValuation(data)); }, []);

  return (
    <div className="animate-fade-up">
      <PageHeader title={t("Reports")} subtitle={t("Purchase, supplier & inventory valuation reports")} icon={FileBarChart} />

      <div className="mb-4 flex flex-wrap gap-2">
        {TABS.map((tb) => (
          <button key={tb.key} onClick={() => setTab(tb.key)} data-testid={`report-tab-${tb.key}`}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-all ${tab === tb.key ? "bg-primary text-white" : "bg-white border border-line text-ink-soft hover:bg-stone-50"}`}>
            <tb.icon size={16} /> {t(tb.label)}
          </button>
        ))}
      </div>

      {tab !== "valuation" && (
        <div className="mb-4 flex flex-wrap items-end gap-3">
          <div><label className="cf-label">{t("From")}</label><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="cf-input" data-testid="report-from" /></div>
          <div><label className="cf-label">{t("To")}</label><input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="cf-input" data-testid="report-to" /></div>
        </div>
      )}

      {/* ---- Purchase Expense ---- */}
      {tab === "expense" && (!expense ? <PageLoader /> : (
        <>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap gap-3">
              <Card className="p-4"><p className="text-xs font-semibold uppercase text-ink-mute">{t("Total Expense")}</p><p className="mt-1 text-xl font-bold text-ink" data-testid="expense-total">{fmtRp(expense.summary.total_expense)}</p></Card>
              <Card className="p-4"><p className="text-xs font-semibold uppercase text-ink-mute">{t("Received POs")}</p><p className="mt-1 text-xl font-bold text-ink">{expense.summary.po_count}</p></Card>
            </div>
            <Button variant="outline" data-testid="export-expense" onClick={() => exportCsv("purchase-expense.csv",
              ["PO No", "Supplier", "Received", "Items", "Units", "Total Cost"],
              expense.rows.map((r) => [r.po_no, r.supplier_name, (r.received_at || "").slice(0, 10), r.item_count, r.total_units, r.total_cost]))}>
              <Download size={16} /> {t("Export CSV")}
            </Button>
          </div>
          <Card className="overflow-hidden p-0"><div className="overflow-x-auto"><table className="w-full text-sm">
            <thead><tr className="border-b border-line text-left text-xs uppercase text-ink-mute">
              <th className="px-5 py-3">{t("PO No")}</th><th className="px-5 py-3">{t("Supplier")}</th><th className="px-5 py-3">{t("Received")}</th>
              <th className="px-5 py-3 text-right">{t("Items")}</th><th className="px-5 py-3 text-right">{t("Total Cost")}</th></tr></thead>
            <tbody>
              {expense.rows.length === 0 && <tr><td colSpan={5} className="px-5 py-10 text-center text-ink-mute">{t("No data in this range")}</td></tr>}
              {expense.rows.map((r) => (
                <tr key={r.po_no} className="border-b border-line/60" data-testid="expense-row">
                  <td className="px-5 py-3 font-mono text-xs font-semibold">{r.po_no}</td>
                  <td className="px-5 py-3">{r.supplier_name}</td>
                  <td className="px-5 py-3 text-xs text-ink-mute">{(r.received_at || "").slice(0, 10)}</td>
                  <td className="px-5 py-3 text-right text-ink-soft">{r.item_count}</td>
                  <td className="px-5 py-3 text-right font-semibold text-ink">{fmtRp(r.total_cost)}</td>
                </tr>
              ))}
            </tbody>
          </table></div></Card>
        </>
      ))}

      {/* ---- Supplier Spending ---- */}
      {tab === "supplier" && (!supplier ? <PageLoader /> : (
        <>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <Card className="p-4"><p className="text-xs font-semibold uppercase text-ink-mute">{t("Total Spend")}</p><p className="mt-1 text-xl font-bold text-ink" data-testid="supplier-total">{fmtRp(supplier.summary.total_spend)}</p></Card>
            <Button variant="outline" data-testid="export-supplier" onClick={() => exportCsv("supplier-spending.csv",
              ["Supplier", "PO Count", "Units", "Spend", "Share %"],
              supplier.rows.map((r) => [r.supplier_name, r.po_count, r.units, r.spend, r.share_pct]))}>
              <Download size={16} /> {t("Export CSV")}
            </Button>
          </div>
          <Card className="overflow-hidden p-0"><div className="overflow-x-auto"><table className="w-full text-sm">
            <thead><tr className="border-b border-line text-left text-xs uppercase text-ink-mute">
              <th className="px-5 py-3">{t("Supplier")}</th><th className="px-5 py-3 text-right">{t("POs")}</th>
              <th className="px-5 py-3 text-right">{t("Spend")}</th><th className="px-5 py-3 text-right">{t("Share")}</th></tr></thead>
            <tbody>
              {supplier.rows.length === 0 && <tr><td colSpan={4} className="px-5 py-10 text-center text-ink-mute">{t("No data in this range")}</td></tr>}
              {supplier.rows.map((r) => (
                <tr key={r.supplier_id} className="border-b border-line/60" data-testid="supplier-row">
                  <td className="px-5 py-3 font-semibold">{r.supplier_name}</td>
                  <td className="px-5 py-3 text-right text-ink-soft">{r.po_count}</td>
                  <td className="px-5 py-3 text-right font-semibold text-ink">{fmtRp(r.spend)}</td>
                  <td className="px-5 py-3 text-right text-ink-soft">{r.share_pct}%</td>
                </tr>
              ))}
            </tbody>
          </table></div></Card>
        </>
      ))}

      {/* ---- Inventory Valuation ---- */}
      {tab === "valuation" && (!valuation ? <PageLoader /> : (
        <>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap gap-3">
              <Card className="p-4"><p className="text-xs font-semibold uppercase text-ink-mute">{t("Total Value")}</p><p className="mt-1 text-xl font-bold text-ink" data-testid="valuation-total">{fmtRp(valuation.summary.total_value)}</p></Card>
              <Card className="p-4"><p className="text-xs font-semibold uppercase text-ink-mute">{t("SKUs")}</p><p className="mt-1 text-xl font-bold text-ink">{valuation.summary.sku_count}</p></Card>
            </div>
            <Button variant="outline" data-testid="export-valuation" onClick={() => exportCsv("inventory-valuation.csv",
              ["Product", "Category", "Unit", "Qty", "Last Cost", "Avg Cost", "Value", "Status"],
              valuation.rows.map((r) => [r.product_name, r.category, r.unit, r.current_stock, r.last_cost, r.average_cost, r.inventory_value, r.status]))}>
              <Download size={16} /> {t("Export CSV")}
            </Button>
          </div>
          <Card className="overflow-hidden p-0"><div className="overflow-x-auto"><table className="w-full text-sm">
            <thead><tr className="border-b border-line text-left text-xs uppercase text-ink-mute">
              <th className="px-5 py-3">{t("Product")}</th><th className="hidden px-5 py-3 sm:table-cell">{t("Category")}</th>
              <th className="px-5 py-3 text-right">{t("Qty")}</th><th className="hidden px-5 py-3 text-right md:table-cell">{t("Last Cost")}</th>
              <th className="px-5 py-3 text-right">{t("Avg Cost")}</th><th className="px-5 py-3 text-right">{t("Value")}</th>
              <th className="px-5 py-3 text-center">{t("Status")}</th></tr></thead>
            <tbody>
              {valuation.rows.map((r) => {
                const st = STOCK_STATUS[r.status] || { label: r.status, tone: "mute" };
                return (
                  <tr key={r.product_id} className="border-b border-line/60" data-testid="valuation-row">
                    <td className="px-5 py-3 font-semibold">{r.product_name}</td>
                    <td className="hidden px-5 py-3 text-ink-soft sm:table-cell">{r.category}</td>
                    <td className="px-5 py-3 text-right">{fmtNum(r.current_stock)}</td>
                    <td className="hidden px-5 py-3 text-right text-ink-soft md:table-cell">{fmtRp(r.last_cost)}</td>
                    <td className="px-5 py-3 text-right text-ink-soft">{fmtRp(r.average_cost)}</td>
                    <td className="px-5 py-3 text-right font-semibold text-ink">{fmtRp(r.inventory_value)}</td>
                    <td className="px-5 py-3 text-center"><Badge tone={st.tone}>{t(st.label)}</Badge></td>
                  </tr>
                );
              })}
            </tbody>
          </table></div></Card>
        </>
      ))}
    </div>
  );
}
